import React, { useRef } from 'react';
import classNames from 'classnames';
import validator from 'validator';
import { Pills } from '@saleshandy/design-system';
import { accessibleOnClick } from '../../../shared/utils/accessible-on-click';

type EmailInputType = {
  name: string;
  label: string;
  value: string;
  emails: string[];
  onChange: (name: string, value: string[]) => void;
  onValueChange: (name: string, value: string) => void;

  showCCInput?: boolean;
  isCCInputVisible?: boolean;
  onCCInputToggle?: VoidFunction;

  showBCCInput?: boolean;
  isBCCInputVisible?: boolean;
  onBCCInputToggle?: VoidFunction;

  isInputDisabled?: boolean;
  isEmailsRemovable?: boolean;
  nonRemovableEmails?: string[];
};

const EmailInput: React.FC<EmailInputType> = ({
  name = '',
  label = '',
  value = '',
  emails = [],
  onChange,
  onValueChange,

  showCCInput = false,
  isCCInputVisible = false,
  onCCInputToggle = () => null,

  showBCCInput = false,
  isBCCInputVisible = false,
  onBCCInputToggle = () => null,

  isInputDisabled = false,
  isEmailsRemovable = false,
  nonRemovableEmails = [],
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(name, event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ' ' || event.key === 'Enter') {
      if (validator.isEmail(value.trim()) && !emails.includes(value.trim())) {
        onChange(name, [...emails, value.trim()]);
        onValueChange(name, '');
      }
    }
  };

  const onEmailRemove = (email) => {
    const filteredArray = emails.filter((e) => e !== email);

    onChange(name, filteredArray);
  };

  const isDisabled = isInputDisabled || emails.length === 10;

  const isValidValue = validator.isEmail(value);

  return (
    <div className="reply-forward-editor--input">
      <span className="label">{label}</span>
      <div
        className="email-pills"
        {...accessibleOnClick(() => inputRef.current?.focus())}
      >
        {emails?.map(
          (email) =>
            email && (
              <Pills
                theme="outline"
                size="md"
                label={email}
                showCloseIcon={
                  isEmailsRemovable && !nonRemovableEmails.includes(email)
                }
                onClose={() => onEmailRemove(email)}
                key={email}
              />
            ),
        )}
        {emails.length <= 10 && (
          <input
            ref={inputRef}
            className="email-input"
            disabled={isDisabled}
            value={value}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
          />
        )}
      </div>
      {(isValidValue || showCCInput || showBCCInput) && (
        <div className="cc-bcc-btn">
          {isValidValue && <span className="press-enter">Press Enter</span>}
          {showCCInput && (
            <span
              className={classNames([{ active: isCCInputVisible }])}
              {...accessibleOnClick(onCCInputToggle)}
            >
              Cc
            </span>
          )}
          {showBCCInput && (
            <span
              className={classNames([{ active: isBCCInputVisible }])}
              {...accessibleOnClick(onBCCInputToggle)}
            >
              Bcc
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailInput;
